import type { TierConfig, TrustTier } from '@/shared/types/RepScoreTypes';

/**
 * Configuration for each RepScore trust tier.
 *
 * 3 Trust Levels:
 * - STARTER: New users, no benefits unlocked
 * - GOOD: First tier unlocked, early pickup at 90%
 * - HIGH: Top tier, early pickup at 80%
 *
 * Note: "At Risk" is NOT a trust level - it's a temporary warning overlay.
 */
export const TIER_CONFIGS: Record<TrustTier, TierConfig> = {
  starter: {
    tier: 'starter',
    displayName: 'STARTER',
    pickupThreshold: 100, // Must pay 100% before pickup
    creditPercentage: 0,
    scoreRange: { min: 0, max: 249 },
    nextTierScore: 250,
    isLocked: true,
    headerGradient: 'from-white via-white to-white',
    badgeColor: 'bg-black-50 text-[#141414]',
  },
  good: {
    tier: 'good',
    displayName: 'GOOD',
    pickupThreshold: 90, // Collect at 90% paid
    creditPercentage: 10,
    scoreRange: { min: 250, max: 499 },
    nextTierScore: 500,
    isLocked: false,
    headerGradient: 'from-white via-white to-white',
    badgeColor: 'bg-motitoBlue text-white',
  },
  high: {
    tier: 'high',
    displayName: 'HIGH',
    pickupThreshold: 80, // Collect at 80% paid
    creditPercentage: 20,
    scoreRange: { min: 500, max: 1000 },
    nextTierScore: null, // Max tier
    isLocked: false,
    headerGradient: 'from-white via-white to-white',
    badgeColor: 'bg-motito-green text-white',
  },
};

/**
 * Configuration for the "at risk" warning state.
 * Temporary overlay, NOT a trust level.
 */
export const AT_RISK_CONFIG = {
  headerGradient: 'from-white via-white to-white',
  badgeColor: 'bg-linter-yellow text-[#141414]',
  gracePeriodHours: 48,
  penaltyPoints: 60, // Points lost for missing grace period
};

/**
 * Get tier from a score level string (from backend).
 * Falls back to 'starter' if unknown.
 */
export function getTierFromScoreLevel(scoreLevel: string): TrustTier {
  const normalizedLevel = scoreLevel.toLowerCase().trim();

  const tierMap: Record<string, TrustTier> = {
    starter: 'starter',
    good: 'good',
    high: 'high',
    // Backend compatibility mappings
    tier_1: 'good',
    tier_2: 'high',
    tier_3: 'high',
    low: 'starter',
    medium: 'good',
    excellent: 'high',
  };

  return tierMap[normalizedLevel] ?? 'starter';
}

/**
 * Get tier from a score value.
 */
export function getTierFromScore(score: number): TrustTier {
  if (score >= 500) return 'high';
  if (score >= 250) return 'good';
  return 'starter';
}
